// src/3-features/measurement/consent/ConsentBanner.tsx
import { useState, useSyncExternalStore } from "react";

import { useT } from "@/3-features/i18n/useT";
import { PATHS } from "@/3-features/navigation/routes";

import { applyAnalyticsConsent, type ConsentChoice, getStoredConsent } from "./consent";

export function ConsentBanner() {
  const { t } = useT();
  // Client-only + mount-gated: the banner is never baked into the prerendered
  // HTML (server snapshot is always false). After hydration it reads the stored
  // choice; it shows only when none is stored, so its buttons always have live
  // handlers and it never flashes for returning users who already chose.
  const noStoredConsent = useSyncExternalStore(
    () => () => {},
    () => getStoredConsent() === null,
    () => false,
  );
  const [dismissed, setDismissed] = useState(false);

  const choose = (v: ConsentChoice) => {
    applyAnalyticsConsent(v);
    setDismissed(true);
  };

  if (dismissed || !noStoredConsent) return null;

  const header = String(t("accept-banner.header"));
  const message = String(t("accept-banner.message"));

  return (
    <div data-testid="consent-banner" className="spacing fixed bottom-0 inset-x-0 pb-2 sm:pb-5 z-[2000]">
      <div className="mx-auto px-2 sm:px-6 lg:px-8">
        <div className="p-2 rounded-lg bg-decblack shadow-lg sm:p-3">
          <div className="flex items-center justify-between flex-wrap">
            <div className="sm:flex-1 flex items-center">
              <div className="flex-col flex">
                <div className="ml-3 font-bold text-xl text-white">
                  {header}
                </div>
                <div className="ml-3 font-medium text-white">
                  {message}
                  <a
                    href={PATHS.privacyPolicy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {" "}
                    {t("accept-banner.privacy-policy-link")}
                  </a>
                </div>
              </div>
            </div>

            <div className="flex gap-2 sm:mt-0 mt-4 sm:w-max w-full sm:mx-0 mx-auto sm:ml-0 ml-2">
              <div className="flex-shrink-0 sm:order-2 sm:mt-0 sm:w-auto">
                <div className="rounded-md shadow-sm">
                  <button
                    className="flex items-center justify-center px-4 py-2 min-h-11 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-decgreen hover:text-slate-200 focus:outline-none focus:shadow-outline transition ease-in-out duration-150"
                    onClick={() => choose("granted")}
                  >
                    {t("accept-banner.accept")}
                  </button>
                </div>
              </div>

              <div className="flex-shrink-0 sm:order-3 sm:ml-2">
                <button
                  className="flex items-center justify-center px-4 py-2 min-h-11 text-sm leading-5 font-medium rounded-md text-decgreen bg-white border-white border-2 hover:text-green-600 focus:outline-none focus:shadow-outline transition ease-in-out duration-150"
                  onClick={() => choose("denied")}
                >
                  {t("accept-banner.reject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
